import is from '@sindresorhus/is';
import { z } from 'zod';

import { jobArchitectureGenerationInfoSchema, jobArchitectureItemSchema } from './job-architecture';
import { versionInformationSchema } from '../../../shared/source/data-contract/job-profile-versions';
import {
    creatorSchema,
    jobArchitectureProfileReviewerSchema,
    jobProfileComponentSchema,
    jobProfileLegacySchema,
} from '../job-profile/job-profile-schema';

import type { SelectedItem } from './job-architecture-store';
import type { SetRequired } from 'type-fest';

export const jobLevelWithTypeSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        jobProfile: jobProfileComponentSchema.optional(),
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
        versionInfo: versionInformationSchema.optional(),
    })
    .transform((jobLevel) => ({
        ...jobLevel,
        type: 'job-level' as const,
    }));

export const jobLevelWithLegacyJobProfileSchemaTransformed = jobArchitectureItemSchema
    .extend({
        actions: z
            .object({
                canAssignReviewer: z.boolean(),
                canEdit: z.boolean(),
                canPublish: z.boolean(),
                canReadDebugInfo: z.boolean(),
            })
            .optional()
            .default({
                canAssignReviewer: false,
                canEdit: false,
                canPublish: false,
                canReadDebugInfo: false,
            }),
        creator: creatorSchema.optional(),
        jobProfile: jobProfileLegacySchema,
        profileGenerationInfo: jobArchitectureGenerationInfoSchema.optional(),
        reviewer: jobArchitectureProfileReviewerSchema.optional(),
    })
    .transform((jobLevel) => ({
        ...jobLevel,
        type: 'job-level' as const,
    }));

export type JobLevel = z.infer<typeof jobLevelWithTypeSchemaTransformed>;

export type JobLevelWithJobProfile = SetRequired<JobLevel, 'jobProfile'>;

export type JobLevelWithLegacyJobProfile = z.infer<typeof jobLevelWithLegacyJobProfileSchemaTransformed>;

export type JobLevelWithReviewer = SetRequired<JobLevel, 'reviewer'>;

export const isJobLevelWithJobProfile = (item: SelectedItem): item is JobLevelWithJobProfile => {
    return item.type === 'job-level' && is.object(item.jobProfile);
};

export const isJobLevelWithReviewer = (item: SelectedItem): item is JobLevelWithReviewer => {
    return item.type === 'job-level' && is.object(item.reviewer);
};
